import type { StarredMessageDto } from '@dijitalapp/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { LinkifiedText, snippet } from '../components/MessageExtras';
import { errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChat, useChatStore } from '../lib/chat-context';
import { conversationTitle } from './Messages';

/** The user's private bookmarks across all conversations, newest first. */
export function StarredPage() {
  const { i18n, me } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const chat = useChat();
  const [items, setItems] = useState<StarredMessageDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!store) return;
    let alive = true;
    store.loadStarred().then(
      (list) => alive && setItems(list),
      (err: unknown) => alive && setError(t(errorMessageKey(err))),
    );
    return () => {
      alive = false;
    };
  }, [store, t]);

  const visible = items?.filter((s) => chat.starred.has(s.message.id)) ?? null;

  return (
    <div className="content-inner stack">
      <h1>{t('chat.starred')}</h1>
      {error && <p className="alert alert-error">{error}</p>}
      {!visible && !error && <p className="muted">{t('app.loading')}</p>}
      {visible?.length === 0 && <p className="muted">{t('chat.noStarred')}</p>}
      {visible?.map(({ message: m }) => {
        const conv = chat.conversations.find((c) => c.id === m.conversationId);
        return (
          <article key={m.id} className="card stack" style={{ gap: 'var(--space-2)' }}>
            <div className="row">
              <strong className="truncate">{conv ? conversationTitle(conv) : '…'}</strong>
              <span className="spacer" />
              <span className="hint">{i18n.formatDate(m.createdAt)}</span>
            </div>
            {m.senderId === me?.id && <span className="hint">{t('chat.you')}</span>}
            <p className="bubble-text">{m.body ? <LinkifiedText text={m.body} /> : snippet(t, m)}</p>
            <div className="row">
              <Link to={`/messages/${m.conversationId}#msg-${m.id}`}>{t('chat.openInChat')}</Link>
              <span className="spacer" />
              <button className="link-button" type="button" onClick={() => void store?.star(m.conversationId, m.id, false)}>
                {t('chat.unstar')}
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}
